import { type AgentMap, getTool, type ProjectSchemaUpdate, TOOLS_UPDATE_KEY, type ToolJSON } from '@takeshape/schema';
import { deepClone } from '@takeshape/util';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import type { ProjectSchemaCompat } from '../../compat/types.js';
import type { ToolEditorFormState } from './types.js';

const renameToolReferences = (agents: AgentMap, oldToolName: string | undefined, newToolName: string): AgentMap =>
  mapValues(agents, (agent) => ({
    ...agent,
    states: mapValues(agent.states, (state) => {
      const execution = state.execution;
      if ((execution.type !== 'chat' && execution.type !== 'generate') || !execution.tools) {
        return state;
      }
      return {
        ...state,
        execution: {
          ...execution,
          tools: execution.tools.map((toolConfig) => {
            if (toolConfig.tool === oldToolName) {
              return {
                ...toolConfig,
                tool: newToolName
              };
            }
            return toolConfig;
          })
        }
      };
    })
  }));

export function getSchemaUpdate(
  projectSchema: ProjectSchemaCompat,
  data: ToolEditorFormState,
  currentToolName?: string
) {
  const { name: toolName, ...newToolProps } = data;

  const oldTool = currentToolName ? getTool(projectSchema, currentToolName) : undefined;
  const oldToolProps = oldTool ? omit(oldTool, 'name') : {};

  // Clone to remove undefined values
  const newTool: ToolJSON = deepClone({
    ...oldToolProps,
    ...newToolProps,
    execution: {
      type: 'graphql',
      ...data.execution
    }
  });

  const toolMap: Record<string, ToolJSON | null> = {
    [toolName]: newTool
  };

  const schemaUpdate: ProjectSchemaUpdate = {
    [TOOLS_UPDATE_KEY]: toolMap
  } satisfies ProjectSchemaUpdate;
  if (currentToolName && currentToolName !== toolName && toolMap) {
    toolMap[currentToolName] = null;
  }

  if (projectSchema.agents && currentToolName !== toolName) {
    schemaUpdate.agents = renameToolReferences(projectSchema.agents, currentToolName, toolName);
  }

  return {
    schemaUpdate,
    toolName
  };
}
